import { ChargingAgreementStationForm, CreateChargingAgreementForm, MARKET_PRICE_COMPENSATION_ID } from "components/ChargingAgreements/modals/EditChargingAgreementModal";
import { ChargingThirdPartyCompensationOption, ChargingThirdPartyCompensationOptionType } from "components/ChargingAgreements/models";
import React from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-date-picker";
import { ReactComponent as CalendarIcon } from '../../../../assets/icons/calendar.svg';
import { validateCommaSeparatedPersonalIdentificationCodes } from "helpers/validator";

interface ChargingStationFormProps {
    form: CreateChargingAgreementForm;
    setForm: (form: CreateChargingAgreementForm) => void;
    station: ChargingAgreementStationForm;
    index: number;
    stationModelOptions: { value: number; label: string }[];
    compensationOptions: ChargingThirdPartyCompensationOption[];
    chargerBasedConfig: boolean;
}

export const EXCLUDED_COMPENSATION_OPTIONS = [
    ChargingThirdPartyCompensationOptionType.NETO,
    ChargingThirdPartyCompensationOptionType.FIXED
]

export default function ChargingStationForm(
    { form, setForm, station, index, stationModelOptions, compensationOptions, chargerBasedConfig }: ChargingStationFormProps
) {
    const currentModelOption = stationModelOptions.find(opt => opt.value === station.stationModelId) || null;

    const compensationSelectOptions = compensationOptions.filter(opt => !EXCLUDED_COMPENSATION_OPTIONS.includes(opt.type)).map(opt => ({ value: opt.id, label: opt.name }));
    const currentCompensationOption = compensationSelectOptions.find(opt => opt.value === station.thirdPartyCompensationOptionId) ?? null;

    const updateStation = (changes: Partial<ChargingAgreementStationForm>) => {
        setForm({
            ...form,
            chargingStations: form.chargingStations.map((s, idx) => idx !== index ? s : { ...s, ...changes })
        });
    };

    const handleChange = (event: any) => {
        updateStation({ [event.target.name]: event.target.value === '' ? undefined : event.target.value });
    };

    return (
        <div className="charging-station-form mb-4">
            {
                chargerBasedConfig && (
                    <>
                        <div className="row mt-2">
                            <div className="col-6">
                                <Form.Label>NOOM ID*</Form.Label>
                                <Form.Control
                                    value={station.externalId ?? ""}
                                    onChange={(e: any) => updateStation({ externalId: e.target.value })}
                                    type="text"
                                    required
                                    isInvalid={form.validated && !station.externalId}
                                    onWheel={(e: any) => e.target.blur()}
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </div>
                            <div className="col-6">
                                <Form.Label>Mudel*</Form.Label>
                                <Select
                                    id={`station-model-select-${index}`}
                                    required
                                    options={stationModelOptions}
                                    value={currentModelOption}
                                    onChange={(opt: any) => updateStation({ stationModelId: opt.value })}
                                    placeholder='Vali mudel'
                                    className={'form-control form-select' + (!station.stationModelId && form.validated ? ' is-invalid' : '')}
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </div>  
                        </div>
                        <div className="row mt-2">
                            <div className="col-6">
                                <Form.Label>Laadija hinnastus*</Form.Label>
                                <Select
                                    required
                                    options={compensationSelectOptions}
                                    value={currentCompensationOption}
                                    onChange={(opt: any) => updateStation({
                                        thirdPartyCompensationOptionId: opt.value,
                                        marketPriceChargingPersonalIdentificationCodes: undefined
                                    })}
                                    placeholder='Vali hinnastus'
                                    className={'w-100 form-control form-select' + (!station.thirdPartyCompensationOptionId && form.validated ? ' is-invalid' : '')}
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </div>
                            {
                                station.thirdPartyCompensationOptionId === MARKET_PRICE_COMPENSATION_ID && (
                                    <div className="col-12 col-md-6">
                                        <Form.Label>Börsihinnaga laadimise klientide isikukoodid</Form.Label>
                                        <Form.Control
                                            value={station.marketPriceChargingPersonalIdentificationCodes?.join(",") ?? ""}
                                            onChange={(e: any) => updateStation({
                                                marketPriceChargingPersonalIdentificationCodes: !e.target.value ? undefined : e.target.value.trim().split(",")
                                            })}
                                            as="textarea"
                                            rows={1}
                                            isInvalid={form.validated && !validateCommaSeparatedPersonalIdentificationCodes(station.marketPriceChargingPersonalIdentificationCodes)}
                                            onWheel={(e: any) => e.target.blur()}
                                        />
                                        <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                    </div>
                                )
                            }
                        </div>
                    </>
                )
            }
            {
                !chargerBasedConfig && (
                    <div className="row mt-3">
                        <div className="col-6">
                            <Form.Label>Mudel*</Form.Label>
                            <Select
                                id={`station-model-select-${index}`}
                                required
                                options={stationModelOptions}
                                value={currentModelOption}
                                onChange={(opt: any) => updateStation({ stationModelId: opt.value })}
                                placeholder='Vali mudel'
                                className={'form-control form-select' + (!station.stationModelId && form.validated ? ' is-invalid' : '')}
                            />
                            <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </div>
                )
            }
            <div className="row mt-2">
                <div className="col-6">
                    <Form.Label>Seerianumber*</Form.Label>
                    <Form.Control
                        value={station.serialNumber}
                        onChange={handleChange}
                        required
                        name="serialNumber"
                    />
                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                </div>
                <div className="col-6">
                    <Form.Label>Isikukood</Form.Label>
                    <Form.Control
                        value={station.personalIdentificationCode ?? ""}
                        onChange={(e: any) => updateStation({ personalIdentificationCode: e.target.value })}
                        type="number"
                        onWheel={(e: any) => e.target.blur()}
                    />
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-6">
                    <Form.Label>Laiuskraad</Form.Label>
                    <Form.Control
                        value={station.latitude}
                        onChange={handleChange}
                        name="latitude"
                    />
                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                </div>
                <div className="col-6">
                    <Form.Label>Pikkuskraad</Form.Label>
                    <Form.Control
                        value={station.longitude}
                        onChange={handleChange}
                        name="longitude"
                    />
                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-6">
                    <Form.Label htmlFor="installation-date">Paigalduskuupäev</Form.Label>
                    <DatePicker
                        className={'d-block'}
                        value={station.installationDate}
                        locale='et'
                        clearIcon={null}
                        calendarIcon={<CalendarIcon />}
                        format='dd.MM.yyyy'
                        onChange={(event: any) => updateStation({ installationDate: new Date(event) })}
                    />
                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                </div>
                <div className="col-6">
                    <Form.Label>Paigalduse lisatasu (€)</Form.Label>
                    <Form.Control
                        value={station.installationExtraFeeEuros}
                        onChange={handleChange}
                        type="number"
                        name="installationExtraFeeEuros"
                        onWheel={(e: any) => e.target.blur()}
                    />
                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-6">
                    <Form.Label>Otsikute arv (tk)</Form.Label>
                    <Form.Control
                        value={station.nozzleCount}
                        onChange={handleChange}
                        type="number"
                        name="nozzleCount"
                        onWheel={(e: any) => e.target.blur()}
                    />
                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                </div>
            </div>
            <button
                type='button'
                className='d-flex align-items-center btn btn-link pl-0 ml-auto'
                onClick={(e) => {
                    e.preventDefault();
                    setForm({
                        ...form,
                        chargingStations: form.chargingStations.filter((_, idx) => idx !== index)
                    });
                }}
            >
                <i className="icon--16 icon__remove--primary d-flex mr-1"></i>
                <span>Eemalda</span>
            </button>
        </div>
    );
}
